import { watch } from "@arcgis/core/core/reactiveUtils";
import MapView from "@arcgis/core/views/MapView";
import {
  WORLD_MAP_FOCUS_ZOOM,
  WORLD_MAP_MAX_ZOOM,
} from "@components/world-map/world-map-constants";
import { WorldMapEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";

interface ReturnProps {
  /** Zooms and centers the map to the user's current geolocation. */
  goToCurrentLocation: () => void;

  /** Zooms the map in by one level. */
  zoomIn: () => void;

  /** Zooms the map out by one level. */
  zoomOut: () => void;

  /** Whether the location button should be disabled (permission denied). */
  isLocationDisabled: boolean;

  /** Whether the zoom out button should be disabled (at min zoom). */
  isZoomOutDisabled: boolean;

  /** Whether the zoom in button should be disabled (at max zoom). */
  isZoomInDisabled: boolean;

  /** Whether to show the permission denied banner (dismissible). */
  shouldShowLocationBanner: boolean;

  /** Dismisses the location permission banner. */
  dismissLocationBanner: () => void;
}

/**
 * Provides map navigation callbacks: geolocation centering and zoom controls.
 * Shows a banner when geolocation permission is denied.
 */
export function useMapNavigation(
  viewRef: RefObject<MapView | null>,
  isViewReady: boolean
): ReturnProps {
  const { trackEvent } = useTrackEvent();
  const [isLocationDisabled, setIsLocationDisabled] = useState(false);
  const [shouldShowLocationBanner, setShouldShowLocationBanner] =
    useState(false);
  const [isZoomOutDisabled, setIsZoomOutDisabled] = useState(false);
  const [isZoomInDisabled, setIsZoomInDisabled] = useState(false);
  const isLocationDeniedRef = useRef(false);
  const isLocationGrantedRef = useRef(false);
  const maxZoomRef = useRef(WORLD_MAP_MAX_ZOOM);

  // Watch the view's zoom to disable zoom buttons at their limits.
  useEffect(() => {
    const view = viewRef.current;
    if (!isViewReady || !view) {
      return;
    }

    function updateButtonStates(): void {
      if (!view) {
        return;
      }
      const lods = view.constraints.effectiveLODs;
      const minZoom = lods
        ? Math.min(...lods.map((lod) => lod.level))
        : view.constraints.minZoom ?? 0;
      const maxZoom = lods
        ? Math.max(...lods.map((lod) => lod.level))
        : view.constraints.maxZoom ?? WORLD_MAP_MAX_ZOOM;
      maxZoomRef.current = maxZoom;
      setIsZoomOutDisabled(view.zoom <= minZoom);
      setIsZoomInDisabled(view.zoom >= maxZoom);
    }

    updateButtonStates();

    const zoomHandle = watch(
      () => view.zoom,
      () => {
        updateButtonStates();
      }
    );

    return () => {
      zoomHandle.remove();
    };
  }, [viewRef, isViewReady]);

  /* Handler for when geolocation permission is denied. Tracks the event once and shows the banner. */
  const handleLocationDenied = useCallback((): void => {
    if (!isLocationDeniedRef.current) {
      trackEvent({ name: WorldMapEvents.locationPermissionDenied });
      isLocationDeniedRef.current = true;
    }
    setIsLocationDisabled(true);
    setShouldShowLocationBanner(true);
  }, [trackEvent]);

  /* Handler for when geolocation permission is granted. Tracks the event once and hides the banner. */
  const handleLocationGranted = useCallback((): void => {
    if (!isLocationGrantedRef.current) {
      trackEvent({ name: WorldMapEvents.locationPermissionGranted });
      isLocationGrantedRef.current = true;
    }
    setShouldShowLocationBanner(false);
  }, [trackEvent]);

  const goToCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        handleLocationGranted();
        void viewRef.current?.goTo({
          center: [position.coords.longitude, position.coords.latitude],
          zoom: WORLD_MAP_FOCUS_ZOOM,
        });
      },
      (error) => {
        if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
          handleLocationDenied();
        }
      }
    );
  }, [viewRef, handleLocationDenied, handleLocationGranted]);

  const dismissLocationBanner = useCallback(() => {
    setShouldShowLocationBanner(false);
  }, []);

  const zoomIn = useCallback(() => {
    const view = viewRef.current;
    if (view) {
      void view.goTo({
        zoom: Math.min(view.zoom + 1, maxZoomRef.current),
      });
    }
  }, [viewRef]);

  const zoomOut = useCallback(() => {
    const view = viewRef.current;
    if (view) {
      void view.goTo({ zoom: view.zoom - 1 });
    }
  }, [viewRef]);

  return {
    goToCurrentLocation,
    zoomIn,
    zoomOut,
    isLocationDisabled,
    isZoomOutDisabled,
    isZoomInDisabled,
    shouldShowLocationBanner,
    dismissLocationBanner,
  };
}
